import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

// Singleton cache to prevent multiple identical requests
let cachedIp: string | null = null;
let fetchPromise: Promise<string | null> | null = null;

const fetchIpOnce = async (): Promise<string | null> => {
  if (cachedIp) return cachedIp;
  if (fetchPromise) return fetchPromise;

  fetchPromise = (async () => {
    try {
      const { data, error } = await supabase.functions.invoke("get-ip");
      if (error) {
        console.error("Error fetching IP:", error);
        return null;
      }
      if (data?.ip) {
        cachedIp = data.ip;
        return data.ip;
      }
      return null;
    } catch (err) {
      console.error("Failed to fetch IP:", err);
      return null;
    } finally {
      fetchPromise = null;
    }
  })();

  return fetchPromise;
};

export const useUserIp = () => {
  const [ip, setIp] = useState<string | null>(cachedIp);

  useEffect(() => {
    if (cachedIp) {
      setIp(cachedIp);
      return;
    }

    // Defer IP fetch to after first paint using requestIdleCallback
    const scheduleFetch = () => {
      fetchIpOnce().then((result) => {
        if (result) setIp(result);
      });
    };

    if ('requestIdleCallback' in window) {
      const id = requestIdleCallback(scheduleFetch);
      return () => cancelIdleCallback(id);
    } else {
      const timer = setTimeout(scheduleFetch, 200);
      return () => clearTimeout(timer);
    }
  }, []);

  return ip;
};

export default useUserIp;
